import { useCallback, useMemo } from 'react';
import {
  createParser,
  parseAsBoolean,
  parseAsString,
  parseAsStringEnum,
  useQueryStates,
  type UrlKeys,
} from 'nuqs';
import {
  DEFAULT_FIXED_STRIKE_CONFIG,
  FixedStrikeConfig,
  FixedStrikeZoomLevel,
  GreeksStatsLookback,
  MatrixDataMode,
  MatrixExpirationsRange,
  MatrixPercentOTMRange,
  PrunePercentRange,
} from 'types/impliedVol';
import { useImpliedVolatilityConfig } from './useImpliedVolatilityConfig';

/**
 * nuqs parser for enums with mixed string/number values.
 * Object.values() on such enums includes reverse-mapped keys for numeric
 * members, so the valid values must be provided explicitly.
 */
function parseMixedEnum<T extends string | number>(validValues: readonly T[]) {
  const set = new Set<string | number>(validValues);
  return createParser<T>({
    parse: (raw: string): T | null => {
      const num = Number(raw);
      if (!isNaN(num) && set.has(num)) {
        return num as T;
      }
      if (set.has(raw)) {
        return raw as T;
      }
      return null;
    },
    serialize: (value: T): string => String(value),
  });
}

// Explicit valid values — Object.values() is unreliable for mixed enums
const EXP_RANGE_VALUES: MatrixExpirationsRange[] = [
  MatrixExpirationsRange['3months'],
  MatrixExpirationsRange['6months'],
  MatrixExpirationsRange['9months'],
  MatrixExpirationsRange['12months'],
  MatrixExpirationsRange.all,
];

const OTM_RANGE_VALUES: MatrixPercentOTMRange[] = [
  MatrixPercentOTMRange['5percent'],
  MatrixPercentOTMRange['10percent'],
  MatrixPercentOTMRange['15percent'],
  MatrixPercentOTMRange['20percent'],
  MatrixPercentOTMRange.all,
];

const PRUNE_RANGE_VALUES: PrunePercentRange[] = [
  PrunePercentRange.none,
  PrunePercentRange.low,
  PrunePercentRange.medium,
  PrunePercentRange.high,
];

// Internal keys — clean names, mapped to URL params via urlKeys
const FS_PARSERS = {
  sym: parseAsString.withDefault(DEFAULT_FIXED_STRIKE_CONFIG.sym),
  mode: parseAsStringEnum<MatrixDataMode>(
    Object.values(MatrixDataMode),
  ).withDefault(DEFAULT_FIXED_STRIKE_CONFIG.dataMode),
  zoom: parseAsStringEnum<FixedStrikeZoomLevel>(
    Object.values(FixedStrikeZoomLevel),
  ).withDefault(DEFAULT_FIXED_STRIKE_CONFIG.zoomLevel),
  exp: parseMixedEnum(EXP_RANGE_VALUES).withDefault(
    DEFAULT_FIXED_STRIKE_CONFIG.expirationRange,
  ),
  otm: parseMixedEnum(OTM_RANGE_VALUES).withDefault(
    DEFAULT_FIXED_STRIKE_CONFIG.percentOTMRange,
  ),
  prune_rows: parseMixedEnum(PRUNE_RANGE_VALUES).withDefault(
    DEFAULT_FIXED_STRIKE_CONFIG.pruneRowsPercent,
  ),
  prune_cols: parseMixedEnum(PRUNE_RANGE_VALUES).withDefault(
    DEFAULT_FIXED_STRIKE_CONFIG.pruneColumnsPercent,
  ),
  hist: parseAsBoolean.withDefault(
    DEFAULT_FIXED_STRIKE_CONFIG.showHistoricalData,
  ),
  hl: parseAsBoolean.withDefault(DEFAULT_FIXED_STRIKE_CONFIG.showHighlights),
  div: parseAsBoolean.withDefault(DEFAULT_FIXED_STRIKE_CONFIG.showDividends),
  sgiv: parseAsBoolean.withDefault(DEFAULT_FIXED_STRIKE_CONFIG.useSpotGammaIV),
  lb: parseAsStringEnum<GreeksStatsLookback>(
    Object.values(GreeksStatsLookback),
  ).withDefault(DEFAULT_FIXED_STRIKE_CONFIG.statsLookback),
};

function createUrlKeys(prefix: string): UrlKeys<typeof FS_PARSERS> {
  return {
    sym: `${prefix}_sym`,
    mode: `${prefix}_mode`,
    zoom: `${prefix}_zoom`,
    exp: `${prefix}_exp`,
    otm: `${prefix}_otm`,
    prune_rows: `${prefix}_prune_rows`,
    prune_cols: `${prefix}_prune_cols`,
    hist: `${prefix}_hist`,
    hl: `${prefix}_hl`,
    div: `${prefix}_div`,
    sgiv: `${prefix}_sgiv`,
    lb: `${prefix}_lb`,
  };
}

export function useFixedStrikeConfig(
  urlKeyPrefix = 'fs',
): [FixedStrikeConfig, (patch: Partial<FixedStrikeConfig>) => void] {
  const urlKeys = useMemo(() => createUrlKeys(urlKeyPrefix), [urlKeyPrefix]);
  const [params, setParams] = useQueryStates(FS_PARSERS, { urlKeys });
  const [ivolConfig] = useImpliedVolatilityConfig();
  const { sym: querySym } = ivolConfig; // shared symbol from implied volatility config

  const config: FixedStrikeConfig = {
    sym: querySym ?? params.sym, // use shared symbol from implied volatility config if present, otherwise use query param
    dataMode: params.mode,
    zoomLevel: params.zoom,
    expirationRange: params.exp,
    percentOTMRange: params.otm,
    pruneRowsPercent: params.prune_rows,
    pruneColumnsPercent: params.prune_cols,
    showHistoricalData: params.hist,
    showHighlights: params.hl,
    showDividends: params.div,
    useSpotGammaIV: params.sgiv,
    statsLookback: params.lb,
  };

  const updateConfig = useCallback(
    (patch: Partial<FixedStrikeConfig>) => {
      const urlPatch: Partial<typeof params> = {};
      if (patch.sym != null) {
        urlPatch.sym = patch.sym;
      }
      if (patch.dataMode != null) {
        urlPatch.mode = patch.dataMode;
      }
      if (patch.zoomLevel != null) {
        urlPatch.zoom = patch.zoomLevel;
      }
      if (patch.expirationRange != null) {
        urlPatch.exp = patch.expirationRange;
      }
      if (patch.percentOTMRange != null) {
        urlPatch.otm = patch.percentOTMRange;
      }
      if (patch.pruneRowsPercent != null) {
        urlPatch.prune_rows = patch.pruneRowsPercent;
      }
      if (patch.pruneColumnsPercent != null) {
        urlPatch.prune_cols = patch.pruneColumnsPercent;
      }
      if (patch.showHistoricalData != null) {
        urlPatch.hist = patch.showHistoricalData;
      }
      if (patch.showDividends != null) {
        urlPatch.div = patch.showDividends;
      }
      if (patch.showHighlights != null) {
        urlPatch.hl = patch.showHighlights;
      }
      if (patch.useSpotGammaIV != null) {
        urlPatch.sgiv = patch.useSpotGammaIV;
      }
      if (patch.statsLookback != null) {
        urlPatch.lb = patch.statsLookback;
      }
      void setParams(urlPatch);
    },
    [setParams],
  );

  return [config, updateConfig];
}
